import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export const Eyebrow = ({ children, className = "" }) => (
  <span className={`eyebrow ${className}`} data-testid="eyebrow">
    {children}
  </span>
);

export const Divider = ({ className = "", light = false }) => (
  <div className={`h-px w-full ${light ? "bg-ink/10" : "bg-white/10"} ${className}`} />
);

export const SectionHeading = ({ eyebrow, title, sub, className = "", light = false }) => (
  <div className={className}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2
      className={`mt-5 font-serif text-3xl md:text-5xl leading-[1.04] tracking-tighter text-balance ${
        light ? "text-ink" : "text-bone"
      }`}
    >
      {title}
    </h2>
    {sub && (
      <p className={`mt-5 max-w-measure text-lg leading-[1.6] ${light ? "text-ink/70" : "text-mist"}`}>{sub}</p>
    )}
  </div>
);

export const CTAButton = ({ to, href, children, variant = "solid", className = "", external, ...props }) => {
  const base =
    "inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium tracking-wide transition-colors duration-300 rounded-none";
  const styles = {
    solid: "bg-saffron text-ink hover:bg-saffron-dark",
    outline: "border border-white/20 text-bone hover:border-saffron hover:text-saffron",
    outlineDark: "border border-ink/20 text-ink hover:border-saffron hover:text-saffron",
    ghost: "text-bone hover:text-saffron px-0 py-0",
    light: "bg-bone text-ink hover:bg-white",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  if (to) return <Link to={to} className={cls} {...props}>{children}</Link>;
  return (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} className={cls} {...props}>
      {children}
    </a>
  );
};

export const ArrowLink = ({ to, href, children, className = "", external = false, light = false, ...props }) => {
  const content = (
    <>
      <span className="link-underline pb-0.5">{children}</span>
      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </>
  );
  const cls = `group inline-flex items-center gap-1.5 text-sm font-medium ${
    light ? "text-ink hover:text-saffron" : "text-bone hover:text-saffron"
  } transition-colors duration-300 ${className}`;
  if (to) return <Link to={to} className={cls} {...props}>{content}</Link>;
  return (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} className={cls} {...props}>
      {content}
    </a>
  );
};

export const Stat = ({ value, label, light = false, className = "" }) => (
  <div className={className}>
    <div className={`font-serif text-4xl md:text-5xl tracking-tight ${light ? "text-ink" : "text-bone"}`}>{value}</div>
    <div className={`mt-3 text-sm leading-snug ${light ? "text-ink/60" : "text-mist"}`}>{label}</div>
  </div>
);

export const Figure = ({ n, caption, children, light = false, className = "" }) => (
  <figure className={`my-12 ${className}`}>
    <div className={`relative overflow-hidden border p-6 md:p-12 ${light ? "border-ink/10 bg-white/50" : "border-white/10 bg-white/[0.015]"}`}>
      {children}
    </div>
    {caption && (
      <figcaption className={`mt-4 font-mono text-xs tracking-wide ${light ? "text-ink/45" : "text-mist"}`}>
        Fig. {n} — {caption}
      </figcaption>
    )}
  </figure>
);

export const Seo = ({ title, description }) => {
  useEffect(() => {
    if (title) document.title = `${title} · DataFrontier`;
    if (description) {
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", description);
    }
    window.scrollTo(0, 0);
  }, [title, description]);
  return null;
};

export const Marquee = ({ items, className = "", light = false }) => (
  <div className={`relative overflow-hidden ${className}`} data-testid="marquee">
    <div className="flex w-max animate-marquee whitespace-nowrap will-change-transform">
      {[...items, ...items].map((it, i) => (
        <span
          key={i}
          className={`mx-10 inline-flex items-center gap-10 font-serif text-2xl md:text-4xl italic ${
            light ? "text-ink/25" : "text-bone/20"
          }`}
        >
          {it}
          <span className="not-italic text-saffron">·</span>
        </span>
      ))}
    </div>
  </div>
);
